/**
 * The interaction sounds, synthesized live through the Web Audio API.
 *
 * Written here rather than taken from a library because no audio library
 * ships a UI palette worth having — they hand you a player and expect you to
 * bring the sounds. The one that does try, cuelume, keeps its recipes private
 * and validates against a fixed list of seventeen names, so tuning a cue means
 * picking the nearest of seventeen rather than changing it. For eight sounds
 * that is the wrong trade: the numbers below are the design, and every one of
 * them is adjustable by ear.
 *
 * What the palette is going for: subtle enough to live on a page somebody
 * browses for minutes at a time. These sit under the interface, not on top of
 * it. Two rules do most of that work:
 *
 * No fast attacks. Anything under about 3ms reads as a click on top of the
 * note — that hard transient is what makes a sound feel rough or cheap. Every
 * cue here fades in over at least 6ms, which is inaudible as a fade and
 * removes the edge entirely.
 *
 * No arpeggios, no echo. A rising run of three notes with a shimmer tail is
 * the sound of something congratulating you. Two notes at most, and the tail
 * is the note's own decay — nothing is fed back.
 *
 * Every pitch is drawn from one A minor pentatonic set, so eight separate
 * cues still read as one instrument rather than eight borrowed noises.
 */

export type Cue =
  | 'sent'
  | 'revealed'
  | 'copied'
  | 'saved'
  | 'failed'
  | 'opened'
  | 'toggled'
  | 'dismissed'

type Note = {
  /** Seconds after the trigger that this note starts. */
  at?: number
  /** Starting pitch, in Hz. */
  hz: number
  /** If set, the pitch slides here across the note. */
  to?: number
  /**
   * Sine unless stated. Square and sawtooth are deliberately never used —
   * their upper harmonics are most of what "harsh" means at these lengths.
   */
  wave?: OscillatorType
  /** Peak amplitude. This is the final loudness — nothing scales it after. */
  peak: number
  /** Fade-in, in seconds. */
  attack: number
  /** Fade-out, in seconds, starting once the attack is done. */
  decay: number
}

/* A minor pentatonic, D3 up to D6 — every pitch in the palette comes from here. */
const D3 = 146.83
const A3 = 220
const B3 = 246.94
const D4 = 293.66
const E4 = 329.63
const G4 = 392
const A4 = 440
const D5 = 587.33
const E5 = 659.25
const A5 = 880
const D6 = 1174.66

const RECIPES: Record<Cue, Note[]> = {
  /**
   * A filter, a sort, a switch. By far the most frequent cue and the one most
   * able to become annoying, so it stays the shortest thing here — under a
   * tenth of a second.
   *
   * Flat rather than falling. A drop in pitch reads as something winding down
   * or being switched off, which is wrong for a control that turns a filter on
   * as often as off; there is nothing for the direction to mean, so it has
   * none.
   *
   * The octave above is what stops it sounding hollow. A lone sine is nothing
   * but its fundamental — no harmonics at all, which is precisely the thin,
   * empty quality — so a quiet second partial rides on top. Short and well
   * under the fundamental, it registers as body and bite rather than as a
   * second note.
   *
   * Lightness is pitch and length together. Raising the pitch alone leaves a
   * sound that is high but still lingers, which lands as shrill rather than
   * light. Both partials move together so the octave stays an octave and the
   * body survives the lift.
   *
   * The upper partial comes down further than the lower one each time this
   * gets quieter. Hearing is at its most sensitive around two to four
   * kilohertz, so as the pair climbs, an octave that keeps its share of the
   * amplitude gains more than its share of the loudness — matching the numbers
   * would slowly turn body into glare.
   */
  toggled: [
    { hz: D5, peak: 0.06, attack: 0.008, decay: 0.05 },
    { hz: D6, peak: 0.02, attack: 0.008, decay: 0.032 },
  ],

  /**
   * Following a game through to its page. Two low notes stepping up, which
   * reads as forward movement; the previous version climbed into the top
   * octave with an echo behind it, which read as a spell being cast.
   */
  opened: [
    { hz: D4, peak: 0.1, attack: 0.01, decay: 0.11 },
    { at: 0.045, hz: G4, peak: 0.09, attack: 0.01, decay: 0.13 },
  ],

  /**
   * Going back, closing something, returning to the top. `opened` run
   * backwards: the same two notes at the same spacing, high to low.
   *
   * It used to glide between them instead of stepping, which fell in pitch but
   * shared none of the shape — a slide and a pair of steps are different
   * gestures, so leaving a page sounded unrelated to arriving at it rather
   * than like its opposite.
   *
   * A little quieter than the way in, and the note it lands on holds longest.
   * Arriving somewhere is worth more attention than leaving it, and a last
   * note that hangs slightly reads as settling rather than stopping.
   */
  dismissed: [
    { hz: G4, peak: 0.09, attack: 0.01, decay: 0.11 },
    { at: 0.045, hz: D4, peak: 0.085, attack: 0.01, decay: 0.14 },
  ],

  /**
   * A key coming out from behind its cover. Root and fifth together with a
   * slow swell — the softest onset in the set, because this one lands while
   * you are reading rather than while you are clicking.
   */
  revealed: [
    { hz: A3, peak: 0.1, attack: 0.018, decay: 0.26 },
    { at: 0.03, hz: E4, peak: 0.075, attack: 0.02, decay: 0.24 },
  ],

  /**
   * The code is on your clipboard. The one place brightness is earned, so it
   * is the only cue that goes near the top octave — but dry and very short,
   * so it sparkles rather than chimes.
   */
  copied: [
    { hz: E5, peak: 0.075, attack: 0.006, decay: 0.09 },
    { at: 0.04, hz: A5, peak: 0.06, attack: 0.006, decay: 0.13 },
  ],

  /** Something saved. A rising fourth: settled, not triumphant. */
  saved: [
    { hz: A4, peak: 0.09, attack: 0.01, decay: 0.16 },
    { at: 0.07, hz: D5, peak: 0.08, attack: 0.012, decay: 0.19 },
  ],

  /**
   * Something failed. Falling, dull and low, on a triangle for a little more
   * weight — deliberately not a buzz. An error tone that makes you flinch is
   * worse than one you simply notice.
   */
  failed: [{ hz: D4, to: B3, wave: 'triangle', peak: 0.085, attack: 0.014, decay: 0.3 }],

  /**
   * A request sent — the one moment on the public site worth marking. Still
   * two notes, but with a low D underneath giving it body the others lack.
   */
  sent: [
    { hz: D3, peak: 0.07, attack: 0.014, decay: 0.3 },
    { hz: D4, peak: 0.085, attack: 0.012, decay: 0.2 },
    { at: 0.08, hz: A4, peak: 0.075, attack: 0.012, decay: 0.26 },
  ],
}

/** Derived from the recipes, so a new cue cannot be missed by the sound page. */
export const CUE_NAMES = Object.keys(RECIPES) as Cue[]

/** Gain ramps cannot reach zero exponentially, so they run to just below it. */
const FLOOR = 0.0001

/** Lets a note ring out fully before its oscillator is stopped. */
const TAIL = 0.04

let context: AudioContext | null = null
let output: AudioNode | null = null

/**
 * The shared output — a gentle lowpass and a limiter, built once.
 *
 * The lowpass is doing real work: it rolls off the top end that survives an
 * oscillator being switched on and off, which is the last of the harshness
 * the slow attacks do not already handle. The limiter only ever engages if
 * cues overlap, and exists so that two at once cannot spike.
 */
function chain(ctx: AudioContext): AudioNode {
  if (output) return output

  const filter = ctx.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.value = 5200

  const limiter = ctx.createDynamicsCompressor()
  limiter.threshold.value = -10
  limiter.knee.value = 8
  limiter.ratio.value = 12
  limiter.attack.value = 0.003
  limiter.release.value = 0.1

  filter.connect(limiter).connect(ctx.destination)
  output = filter

  return filter
}

function audioContext(): AudioContext | null {
  if (context) return context
  if (typeof window === 'undefined') return null

  const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null

  try {
    context = new Ctor()
  } catch {
    // Blocked, or no audio device. Every cue becomes a no-op, which is fine.
    return null
  }

  return context
}

function renderNote(ctx: AudioContext, destination: AudioNode, note: Note, start: number): void {
  const at = start + (note.at ?? 0)
  const length = note.attack + note.decay

  const oscillator = ctx.createOscillator()
  oscillator.type = note.wave ?? 'sine'
  oscillator.frequency.setValueAtTime(note.hz, at)
  if (note.to !== undefined) oscillator.frequency.exponentialRampToValueAtTime(note.to, at + length)

  const gain = ctx.createGain()
  gain.gain.setValueAtTime(FLOOR, at)
  gain.gain.exponentialRampToValueAtTime(note.peak, at + note.attack)
  gain.gain.exponentialRampToValueAtTime(FLOOR, at + length)

  oscillator.connect(gain).connect(destination)

  // Tearing down on the oscillator's own end event rather than a timer keeps
  // the cleanup tied to the audio clock, which is the only clock that knows
  // when the note actually finished.
  oscillator.onended = () => gain.disconnect()

  oscillator.start(at)
  oscillator.stop(at + length + TAIL)
}

/**
 * Plays a cue. Safe to call anywhere — it builds the audio context on first
 * use, resumes one the browser started suspended, and does nothing at all
 * when there is no Web Audio to speak of.
 */
export function render(name: Cue): void {
  // Nothing may play before the page has been interacted with. Checking here
  // rather than letting the context fail means no AudioContext is created for
  // a visitor who never turns sound on.
  if (typeof navigator !== 'undefined' && navigator.userActivation?.hasBeenActive === false) return

  const ctx = audioContext()
  if (!ctx) return

  const notes = RECIPES[name]
  const start = () => {
    const destination = chain(ctx)
    for (const note of notes) renderNote(ctx, destination, note, ctx.currentTime)
  }

  if (ctx.state === 'running') {
    start()
    return
  }

  try {
    void ctx.resume().then(start, () => {})
  } catch {
    // Some browsers throw synchronously when audio is blocked outright.
  }
}
